import {
  Camera,
  Crown,
  PencilSimple,
  SignOut,
  UserMinus,
  UserPlus,
} from '@phosphor-icons/react';
import { useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { PublicUser } from '@sirocco/shared';
import { messages } from '../../api/endpoints';
import { ScreenError, ScreenLoader, SubScreen } from '../../components/layout/Screens';
import { Avatar, GroupAvatar } from '../../components/ui/Avatar';
import { Button } from '../../components/ui/Button';
import { ConfirmDialog, useConfirm } from '../../components/ui/Confirm';
import { PhotoPicker } from '../../components/ui/PhotoPicker';
import { PhotoViewer } from '../../components/ui/PhotoViewer';
import { Sheet, SheetAction } from '../../components/ui/Sheet';
import { useAsync } from '../../hooks/useAsync';
import { useLongPress } from '../../hooks/useLongPress';
import { useSession } from '../../store/SessionContext';
import { shortDate } from '../../utils/format';
import { profilePath } from '../../utils/routes';
import { HandOverSheet, needsSuccessor } from './HandOverSheet';
import { t } from '../../i18n';

/**
 * Infos du groupe : sa carte d'identite, ses reglages, ses membres.
 * C'est aussi la seule porte de sortie d'un groupe.
 */
export function GroupInfoScreen() {
  const { conversationId = '' } = useParams();
  const { user } = useSession();
  const navigate = useNavigate();
  const [leaving, setLeaving] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [viewed, setViewed] = useState<string | null>(null);
  // On ne revient pas dans un groupe qu'on a quitte : la question se pose.
  const { pending, request, cancel, confirm } = useConfirm();

  const { data, loading, error, reload } = useAsync(
    () => messages.conversation(conversationId),
    [conversationId],
    `group:${conversationId}`,
  );

  /*
    Le titre se modifie ici meme, sans changer d'ecran : c'est un champ,
    pas une page. `renaming` porte le brouillon — a `null`, on ne renomme
    pas.
  */
  const [renaming, setRenaming] = useState<string | null>(null);
  /* La description s'ecrit comme le nom : a sa place, sans formulaire. */
  const [describing, setDescribing] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function saveDescription() {
    if (describing === null) return;
    const wanted = describing.trim();

    if (wanted === (data?.description ?? '')) {
      setDescribing(null);
      return;
    }

    setSaving(true);
    setFailure(null);
    try {
      await messages.describeGroup(conversationId, wanted);
      setDescribing(null);
      reload();
    } catch (cause) {
      setFailure(cause instanceof Error ? cause.message : t.messages.groupe.descriptionImpossible);
    } finally {
      setSaving(false);
    }
  }

  async function saveTitle() {
    if (renaming === null) return;
    const wanted = renaming.trim();

    // Rien tape, ou rien change : on referme sans ecrire.
    if (!wanted || wanted === data?.title) {
      setRenaming(null);
      return;
    }

    setSaving(true);
    setFailure(null);
    try {
      await messages.renameGroup(conversationId, wanted);
      setRenaming(null);
      reload();
    } catch (cause) {
      setFailure(cause instanceof Error ? cause.message : t.messages.groupe.renommageImpossible);
    } finally {
      setSaving(false);
    }
  }

  /*
    Le membre dont la feuille d'actions est ouverte.

    L'etat vit ici, et non dans la ligne : posee dans la zone defilante,
    une feuille s'ouvrirait sous le pli, `position: absolute` s'ancrant a
    cette zone.
  */
  const [held, setHeld] = useState<PublicUser | null>(null);
  /* La ligne tenue : c'est d'elle que sort la fenetre. */
  const heldAnchor = useRef<HTMLElement | null>(null);
  /*
    La feuille du depart d'un administrateur : il choisit d'abord son
    repreneur, et c'est ce choix qui declenche la sortie.
  */
  const [handOverOpen, setHandOverOpen] = useState(false);

  async function promote(member: PublicUser) {
    setHeld(null);
    setFailure(null);
    try {
      await messages.setGroupAdmin(conversationId, member.id);
      reload();
    } catch (cause) {
      setFailure(cause instanceof Error ? cause.message : t.messages.groupe.passerImpossible);
    }
  }

  async function exclude(member: PublicUser) {
    setHeld(null);
    setFailure(null);
    try {
      await messages.removeGroupMember(conversationId, member.id);
      reload();
    } catch (cause) {
      setFailure(cause instanceof Error ? cause.message : t.messages.groupe.retirerImpossible);
    }
  }

  async function changePhoto(avatarUrl: string) {
    setFailure(null);
    try {
      await messages.setGroupPhoto(conversationId, avatarUrl);
      reload();
    } catch (cause) {
      setFailure(cause instanceof Error ? cause.message : t.messages.groupe.photoImpossible);
    }
  }

  async function leave(successorId?: string) {
    setLeaving(true);
    setFailure(null);
    try {
      await messages.leaveGroup(conversationId, successorId);
      // Le groupe quitte n'existe plus pour moi : pas de retour en arriere.
      navigate('/app/messages', { replace: true });
    } catch (cause) {
      setFailure(cause instanceof Error ? cause.message : t.messages.groupe.quitterImpossible);
      setLeaving(false);
    }
  }

  /*
    Partir demande d'abord de passer la main. Les pouvoirs — retirer un
    membre, nommer un successeur — n'appartiennent qu'a l'administrateur :
    les emporter en sortant laisserait le groupe sans personne pour
    l'ouvrir. `needsSuccessor` dit quand la question se pose, et la meme
    regle vaut depuis la liste des conversations.
  */
  function onLeave() {
    if (data && needsSuccessor(data, user?.id)) {
      setHandOverOpen(true);
      return;
    }
    request(t.messages.groupe.quitterQ, () => void leave());
  }

  return (
    <SubScreen
      title={t.messages.commun.infosGroupe}
      bottomBar={
        <Button
          variant="danger"
          block
          icon={<SignOut className="sens-x" size={20} />}
          loading={leaving}
          onClick={onLeave}
        >
          {t.messages.commun.quitterGroupe}
        </Button>
      }
      overlay={
        <>
          <PhotoViewer src={viewed} onClose={() => setViewed(null)} />
          <HandOverSheet
            open={handOverOpen}
            members={data?.members ?? []}
            meId={user?.id}
            orphan={!data?.adminId}
            onClose={() => setHandOverOpen(false)}
            onPick={(member) => {
              setHandOverOpen(false);
              request(
                t.messages.commun.laisserEtPartirQ(member.username),
                () => void leave(member.id),
              );
            }}
          />

          {/* Sans titre : la fenetre sort de la ligne du membre vise. */}
          <Sheet open={held !== null} anchor={heldAnchor} onClose={() => setHeld(null)}>
            {held && (
              <>
                <SheetAction icon={<Crown size={22} />} onClick={() => void promote(held)}>
                  {t.messages.groupe.nommerAdmin}
                </SheetAction>

                <SheetAction
                  icon={<UserMinus size={22} />}
                  tone="danger"
                  onClick={() =>
                    request(t.messages.groupe.retirerQ(held.username), () => void exclude(held))
                  }
                >
                  {t.messages.groupe.retirer}
                </SheetAction>
              </>
            )}
          </Sheet>
          <ConfirmDialog pending={pending} onCancel={cancel} onConfirm={confirm} />
        </>
      }
    >
      {loading && <ScreenLoader />}
      {error && <ScreenError message={error} onRetry={reload} />}
      {failure && <p className="field__error">{failure}</p>}

      {data && (
        <>
          <div className="ms-group-head">
            {/* Seul l'admin change la photo ; les autres la regardent. */}
            {data.adminId === user?.id ? (
              <PhotoPicker
                preset="avatar"
                label={t.messages.groupe.changerPhoto}
                className="ms-group-head__photo"
                onPicked={changePhoto}
                onError={setFailure}
              >
                <GroupAvatar size={96} rounded photoUrl={data.avatarUrl} />
                <span className="ms-group-head__badge" aria-hidden="true">
                  <Camera size={16} weight="fill" />
                </span>
              </PhotoPicker>
            ) : data.avatarUrl ? (
              <button
                className="ms-group-head__photo"
                aria-label={t.messages.groupe.voirPhoto}
                onClick={() => setViewed(data.avatarUrl)}
              >
                <GroupAvatar size={96} rounded photoUrl={data.avatarUrl} />
              </button>
            ) : (
              <GroupAvatar size={96} rounded />
            )}

            {renaming === null ? (
              <p className="ms-group-head__name">
                <span className="ms-group-head__title">{data.title}</span>
                {/* Le nom aussi n'appartient qu'a l'administrateur. */}
                {data.adminId === user?.id && (
                <button
                  className="ms-group-head__rename"
                  aria-label={t.messages.groupe.modifierNom}
                  onClick={() => setRenaming(data.title)}
                >
                  <PencilSimple size={16} />
                </button>
                )}
              </p>
            ) : (
              /*
                Le champ remplace le titre a sa place exacte, a la meme
                taille : on corrige un mot, on ne remplit pas un formulaire.
                Il part au clavier comme au doigt pose ailleurs — trois
                facons de valider, aucun bouton de plus.
              */
              <input
                className="ms-group-head__input"
                value={renaming}
                autoFocus
                maxLength={60}
                disabled={saving}
                onChange={(event) => setRenaming(event.target.value)}
                onBlur={() => void saveTitle()}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') void saveTitle();
                  if (event.key === 'Escape') setRenaming(null);
                }}
              />
            )}
            <p className="ms-group-head__meta">
              {t.messages.groupe.meta(data.members.length, shortDate(data.createdAt))}
            </p>

            {/*
              Ajouter quelqu'un se range avec le compte des membres, pas
              plus bas : c'est la meme phrase, dite au present puis au
              futur. Une etiquette suffit — un bandeau pleine largeur
              annoncait un reglage la ou il n'y a qu'un geste.
            */}
            {/*
              Sauf pour le groupe d'une sortie ou d'une association.

              On y entre en rejoignant l'evenement ou l'association,
              jamais sur invitation — l'organisateur et le chef compris.
              Proposer le geste serait promettre ce que les regles
              refusent.
            */}
            {!data.eventId && !data.asso && (
              <Link
                to={`/app/messages/${conversationId}/add`}
                className="ms-group-head__ajout"
              >
                <UserPlus size={15} />
                {t.messages.groupe.ajouterMembre}
              </Link>
            )}
            {/*
              La description n'appartient qu'a l'administrateur, comme le
              nom. Les autres la lisent ; lui la corrige a sa place, du
              meme geste et sans quitter l'ecran.
            */}
            {describing === null ? (
              data.description ? (
                <p className="ms-group-head__about">
                  {data.description}
                  {data.adminId === user?.id && (
                    <button
                      className="ms-group-head__rename"
                      aria-label={t.messages.groupe.modifierDescription}
                      onClick={() => setDescribing(data.description ?? '')}
                    >
                      <PencilSimple size={14} />
                    </button>
                  )}
                </p>
              ) : (
                data.adminId === user?.id && (
                  <button
                    className="ms-group-head__ajouter"
                    onClick={() => setDescribing('')}
                  >
                    {t.messages.groupe.ajouterDescription}
                  </button>
                )
              )
            ) : (
              <textarea
                className="ms-group-head__about-input"
                value={describing}
                autoFocus
                rows={2}
                maxLength={200}
                placeholder={t.messages.groupe.deQuoi}
                disabled={saving}
                onChange={(event) => setDescribing(event.target.value)}
                onBlur={() => void saveDescription()}
                onKeyDown={(event) => {
                  if (event.key === 'Escape') setDescribing(null);
                }}
              />
            )}
          </div>

          <h2 className="ms-section">{t.messages.commun.membres}</h2>
          {data.members.map((member) => (
            <MemberRow
              key={member.id}
              member={member}
              isAdmin={member.id === data.adminId}
              /* L'appui long n'a de sens que pour l'admin, et pas sur lui. */
              onHold={
                data.adminId === user?.id && member.id !== user?.id
                  ? (trigger: HTMLElement) => {
                      heldAnchor.current = trigger;
                      setHeld(member);
                    }
                  : undefined
              }
            />
          ))}
        </>
      )}
    </SubScreen>
  );
}

/**
 * Une ligne de la liste des membres.
 *
 * Un appui mene au profil ; un appui long ouvre les actions, mais
 * seulement pour l'administrateur et jamais sur lui-meme. Sans `onHold`,
 * la ligne n'est qu'un lien — le geste long ne fait alors rien plutot que
 * d'ouvrir une feuille vide.
 */
function MemberRow({
  member,
  isAdmin,
  onHold,
}: {
  member: PublicUser;
  isAdmin: boolean;
  onHold?: (trigger: HTMLElement) => void;
}) {
  const { user } = useSession();
  const hold = useLongPress((trigger) => onHold?.(trigger));

  return (
    <Link to={profilePath(member.id, user?.id)} className="ms-member" {...(onHold ? hold : {})}>
      <Avatar user={member} size={38} />
      {/* L'admin se dit par une couronne collee au nom, plus par une etiquette. */}
      <span className="ms-member__name">
        {member.username}
        {isAdmin && (
          <Crown
            size={16}
            className="ms-member__couronne"
            role="img"
            aria-label={t.messages.groupe.admin}
          />
        )}
      </span>
    </Link>
  );
}
